import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Send, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { announcementAPI } from '../lib/api';   // or wherever you put it
import { toast } from 'sonner';

interface Announcement {
  id: string;
  title: string;
  message: string;
  target: 'ALL' | 'SPECIFIC_COURSE';
  targetCourseId?: string | null;
  createdAt: string;
  admin?: { name?: string };   // optional if backend returns admin info
}

export function AnnouncementsSection() {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [showDialog, setShowDialog] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null);
  
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    target: 'ALL' as 'ALL' | 'SPECIFIC_COURSE',
    targetCourseId: '',
  });

  // Load announcements on mount
  useEffect(() => {
    loadAnnouncements();
  }, []);

  const loadAnnouncements = async () => {
    try {
      setLoading(true);
      const response = await announcementAPI.getAnnouncements();
      
      // Handle both { success: true, data: [...] } and direct array
      const data = response?.success ? response.data : response;
      setAnnouncements(Array.isArray(data) ? data : []);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to load announcements');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingAnnouncement(null);
    setFormData({ title: '', message: '', target: 'ALL', targetCourseId: '' });
    setShowDialog(true);
  };

  const handleEdit = (announcement: Announcement) => {
    setEditingAnnouncement(announcement);
    setFormData({
      title: announcement.title,
      message: announcement.message,
      target: announcement.target,
      targetCourseId: announcement.targetCourseId || '',
    });
    setShowDialog(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) return;

    try {
      await announcementAPI.deleteAnnouncement(id);
      setAnnouncements(prev => prev.filter(a => a.id !== id));
      toast.success('Announcement deleted successfully');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to delete announcement');
    }
  };

  const handleSave = async () => {
    if (!formData.title.trim() || !formData.message.trim()) {
      toast.error('Title and message are required');
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        title: formData.title.trim(),
        message: formData.message.trim(),
        target: formData.target,
        ...(formData.target === 'SPECIFIC_COURSE' && formData.targetCourseId && {
          targetCourseId: formData.targetCourseId,
        }),
      };

      let response;
      if (editingAnnouncement) {
        response = await announcementAPI.updateAnnouncement(editingAnnouncement.id, payload);
      } else {
        response = await announcementAPI.createAnnouncement(payload);
      }

      const savedAnnouncement = response?.success ? response.data : response;

      if (editingAnnouncement) {
        // Update in place
        setAnnouncements(prev =>
          prev.map(a => a.id === editingAnnouncement.id ? savedAnnouncement : a)
        );
        toast.success('Announcement updated successfully');
      } else {
        // Prepend new announcement
        setAnnouncements(prev => [savedAnnouncement, ...prev]);
        toast.success('Announcement posted successfully');
      }

      setShowDialog(false);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to save announcement');
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const closeDialog = () => {
    setShowDialog(false);
    setEditingAnnouncement(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Announcements</h2>
          <p className="text-gray-500 mt-1">Communicate with your students</p>
        </div>
        <Button onClick={handleCreate} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="w-4 h-4 mr-2" /> New Announcement
        </Button>
      </div>

      {announcements.length === 0 ? (
        <Card className="p-12">
          <div className="text-center">
            <p className="text-gray-500 mb-4">No announcements yet. Create your first one!</p>
            <Button onClick={handleCreate}>
              <Plus className="w-4 h-4 mr-2" /> Create Announcement
            </Button>
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          {announcements.map((announcement) => (
            <Card key={announcement.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle>{announcement.title}</CardTitle>
                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                      <span>{new Date(announcement.createdAt).toLocaleDateString()}</span>
                      <span>•</span>
                      <span>By {announcement.admin?.name || 'Admin'}</span>
                      <span>•</span>
                      <span className="text-blue-600">
                        {announcement.target === 'ALL' ? 'All Students' : 'Specific Course'}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(announcement)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(announcement.id)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 whitespace-pre-wrap">{announcement.message}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingAnnouncement ? 'Edit Announcement' : 'New Announcement'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Title</Label>
              <Input
                placeholder="Announcement title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </div>

            <div>
              <Label>Message</Label>
              <Textarea
                placeholder="Your announcement message..."
                rows={6}
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              />
            </div>

            <div>
              <Label>Target Audience</Label>
              <Select 
                value={formData.target} 
                onValueChange={(value: 'ALL' | 'SPECIFIC_COURSE') => 
                  setFormData({ ...formData, target: value, targetCourseId: '' })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select target" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Students</SelectItem>
                  <SelectItem value="SPECIFIC_COURSE">Specific Course</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.target === 'SPECIFIC_COURSE' && (
              <div>
                <Label>Course ID (for SPECIFIC_COURSE)</Label>
                <Input
                  placeholder="Enter course ID"
                  value={formData.targetCourseId}
                  onChange={(e) => setFormData({ ...formData, targetCourseId: e.target.value })}
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeDialog} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={submitting}>
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <Send className="w-4 h-4 mr-2" />
              {editingAnnouncement ? 'Update' : 'Post'} Announcement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}